import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { X, ShieldCheck, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { Sheet } from "@/shared/motion";

/**
 * AddCardSheet — in-app, PCI-safe card-adding bottom sheet.
 *
 * Opens from /payment-methods. Hits POST /api/me/payment-methods/setup-intent
 * to mint a SetupIntent on the rider's Stripe customer, then mounts
 * Stripe Elements inline so the rider can enter card details once and
 * have them saved to their account for all future bookings.
 *
 * After a successful setup, the parent's `onSuccess` callback fires —
 * the parent reloads the saved-cards list and optionally promotes the
 * new card to default if it's the rider's first card.
 *
 * Card data NEVER touches Link's servers. Stripe.js tokenises directly
 * against Stripe's API; we only receive the resulting PaymentMethod ID.
 */
const stripeSingletons = new Map();
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (!stripeSingletons.has(publishableKey)) {
    stripeSingletons.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeSingletons.get(publishableKey);
}

export default function AddCardSheet({ open, onClose, onSuccess }) {
  const [intent, setIntent] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Re-mint a SetupIntent every time the sheet opens. Stale client_secrets
  // can't be reused once confirmed and the network call is cheap.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setIntent(null);
      setStripePromise(null);
      setError(null);
      setLoading(true);
      try {
        const { data } = await api.post("/me/payment-methods/setup-intent");
        if (cancelled) return;
        if (!data?.client_secret || !data?.publishable_key) {
          throw new Error("Missing Stripe configuration");
        }
        setIntent(data);
        setStripePromise(getStripePromise(data.publishable_key));
      } catch (e) {
        if (cancelled) return;
        const msg = formatApiError(e?.response?.data?.detail) || e.message || "Could not start card setup";
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const clientSecret = intent?.client_secret || null;
  const elementsOptions = useMemo(() => {
    if (!clientSecret) return null;
    // Match the existing checkout sheet's navy/orange Stripe Elements theme
    // so /payment-methods feels visually consistent with /schedule's
    // slide-to-confirm.
    return {
      clientSecret,
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#ed6500",
          colorBackground: "#0A1128",
          colorText: "#F9F7F4",
          colorTextSecondary: "rgba(249,247,244,0.6)",
          colorTextPlaceholder: "rgba(249,247,244,0.4)",
          colorDanger: "#ff6b6b",
          fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
          fontSizeBase: "15px",
          borderRadius: "14px",
          spacingUnit: "4px",
        },
        rules: {
          ".Input": {
            backgroundColor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#F9F7F4",
          },
          ".Input:focus": {
            border: "1px solid #ed6500",
            boxShadow: "0 0 0 2px rgba(237,101,0,0.25)",
          },
          ".Input--invalid": { border: "1px solid #ff6b6b" },
          ".Label": {
            fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
            fontSize: "10px",
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            color: "rgba(249,247,244,0.55)",
            fontWeight: 500,
          },
        },
      },
    };
  }, [clientSecret]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open && !loading && !error && !elementsOptions) {
    // Skip mounting when not in use AND fully clean — but only when SAFE
    // (Sheet primitive handles its own AnimatePresence enter/exit).
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      className="bg-[#0A1128]/95 backdrop-blur-xl text-[#F9F7F4] border border-white/10 sm:max-w-md sm:mx-auto sm:rounded-[24px]"
      maxHeight="92dvh"
      rounded="rounded-t-[24px]"
      testid="add-card-sheet"
      ariaLabel="Add card"
    >
      <div className="sm:hidden flex justify-center pt-2.5">
        <div className="w-10 h-1 rounded-full bg-white/20" />
      </div>

      <header className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-white/8">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] font-medium">Add a card</div>
            <div className="font-serif text-[24px] leading-tight mt-1.5">Save for future rides</div>
            <div className="text-[12px] text-white/55 mt-1">
              We&apos;ll use it automatically on your next booking.
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 -mr-1 -mt-1 rounded-full text-white/60 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="add-card-sheet-close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </header>

        <div className="px-6 py-6">
          {loading && (
            <div className="flex items-center justify-center py-16" data-testid="add-card-sheet-loading">
              <Loader2 size={22} strokeWidth={1.6} className="animate-spin text-white/60" />
            </div>
          )}
          {error && (
            <div className="py-8" data-testid="add-card-sheet-error">
              <div className="font-serif text-xl mb-1">We couldn&apos;t start card setup.</div>
              <div className="text-[13px] text-white/60 leading-relaxed mb-5">{error}</div>
              <button
                onClick={onClose}
                className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          )}
          {!loading && !error && elementsOptions && stripePromise && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <AddCardInnerForm
                intent={intent}
                onSuccess={(pmId) => {
                  onSuccess?.(pmId);
                  onClose?.();
                }}
              />
            </Elements>
          )}
        </div>

        <footer className="px-6 pb-4 pt-3 flex items-center justify-center gap-2 text-[9px] tracking-[0.28em] uppercase text-white/40 border-t border-white/8">
          <ShieldCheck size={11} strokeWidth={1.6} /> Secured by Stripe · PCI compliant
        </footer>
    </Sheet>
  );
}

function AddCardInnerForm({ intent, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const successRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting || successRef.current) return;
    setSubmitting(true);
    setErr(null);
    try {
      const { error: sErr, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        // SCA challenges land back on /payment-methods, no special hash needed.
        confirmParams: { return_url: `${window.location.origin}/payment-methods` },
      });
      if (sErr) throw sErr;
      if (setupIntent && setupIntent.status !== "succeeded") {
        throw new Error(`Card setup status: ${setupIntent.status}`);
      }
      successRef.current = true;
      setDone(true);
      toast.success("Card saved");
      // Brief success state so the rider sees confirmation before the
      // sheet closes and the parent list refreshes.
      setTimeout(() => onSuccess?.(setupIntent?.payment_method), 900);
    } catch (ex) {
      const msg = ex?.message || "Couldn't save your card";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="py-12 text-center" data-testid="add-card-sheet-success">
        <div className="mx-auto w-12 h-12 rounded-full border border-[#ed6500] bg-[#ed6500]/10 flex items-center justify-center mb-5">
          <Check size={20} strokeWidth={1.8} className="text-[#ed6500]" />
        </div>
        <div className="font-serif text-[24px] leading-tight mb-2">Card saved</div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-white/50">Ready for your next ride</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="add-card-sheet-form">
      <PaymentElement
        options={{
          layout: "tabs",
          // Restrict to card — Apple/Google Pay don't make sense for
          // "save a card to my wallet" (they're one-tap-at-checkout flows).
          // Also force card-only ordering so Stripe doesn't auto-promote
          // the US Bank / Stripe Link tab (the SetupIntent is created
          // server-side with payment_method_types=['card'], but Stripe
          // surfaces additional methods client-side unless ordered).
          paymentMethodOrder: ["card"],
          wallets: { applePay: "never", googlePay: "never" },
        }}
      />
      {err && (
        <div className="text-[12px] text-[#ff6b6b] leading-relaxed" data-testid="add-card-sheet-err">{err}</div>
      )}
      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.32em] uppercase font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99] flex items-center justify-center gap-2"
        data-testid="add-card-sheet-submit"
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 size={14} strokeWidth={1.8} className="animate-spin" /> Saving
          </span>
        ) : (
          <span>Save card</span>
        )}
      </button>
    </form>
  );
}
